import React, { useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { colors, Grid, Card, CardContent, Typography } from '@mui/material';
import { Page, MapCapacityGeneric, Header } from 'components';
import L from 'leaflet';
import _ from 'underscore';
import SquareIcon from '@mui/icons-material/Square';
import { enqueueSnackbar } from 'actions';
import { onUpdateLegend } from 'components/Map/actions/MapActions';
import {
	SetFormState, ResetFields,
	GetDriveTestsCities, GetFailuresFilters, SetSelectedDates,
	GetFailuresFiltersSideCompare, SetSelectedDatesCompare,
	GetSitesIds, ToggleCarriersColorsDialog, ToggleCarriersColorsPickerDialog, GetUtilizationDates, SetSelectedUtilizationDate
} from '../actions/DualMapActionsGeneric';
import CompareBox from './components/CompareBox';
import FiltersTop from './components/FiltersTop';
import FiltersTopCompare from './components/FiltersTopCompare';
import FiltersSide from './components/FiltersSide';
import FiltersSideCompare from './components/FiltersSideCompare';
import AdditionalInfo from './components/AdditionalInfo';
import CarriersColorsDialog from './components/Components/CarriersColorsDialog';
import CarriersColorsPickerDialog from './components/Components/CarriersColorsPickerDialog';
import UtilizationDates from './components/UtilizationDates';
import UtilizationDatesCompare from './components/UtilizationDatesCompare';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const DualMapGeneric = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.dualMapReducerGeneric.formState);
	const maps = useSelector(state => state.mapReducer.maps);

	// const topology4gDatacompare = useSelector((state) => state.dualMapReducerGeneric.topology4gDataCompare);
	// console.log(topology4gDatacompare);

	// Map
	const selectedDates = useSelector(state => state.dualMapReducerGeneric.selectedDates);
	const mapOne = maps.filter(item => item.id === 'dual-map')[0];
	const driveTest = useSelector(state => state.dualMapReducerGeneric.driveTest);

	// Map Compare
	const selectedDatesCompare = useSelector(state => state.dualMapReducerGeneric.selectedDatesCompare);
	const mapTwo = maps.filter(item => item.id === 'dual-map-compare')[0];
	const driveTestCompare = useSelector(state => state.dualMapReducerGeneric.driveTestCompare);

	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);
	const userOperator = useSelector(state => state.session.user.operator);

	const carriersColorsDialogVisibility = useSelector((state) => state.dualMapReducerGeneric.carriersColorsDialogVisibility);
	const carriersColorsPickerDialogVisibility = useSelector((state) => state.dualMapReducerGeneric.carriersColorsPickerDialogVisibility);
	const currentColorCode = useSelector((state) => state.dualMapReducerGeneric.currentColorCode);

	const _getData = useCallback(() => {
		dispatch(GetSitesIds());
		dispatch(ResetFields());
		dispatch(GetDriveTestsCities(userId, userOperator));
		// Map
		dispatch(GetFailuresFilters(role));
		dispatch(SetSelectedDates([]));
		// Map Compare
		dispatch(GetFailuresFiltersSideCompare(role));
		dispatch(SetSelectedDatesCompare([]));
	}, []);

	useEffect(() => {
		_getData();
	}, [_getData]);

	useEffect(() => {
		dispatch(GetUtilizationDates());
	}, []);

	const makeDetailsTable = (item) => {
		const table = `<table class="table table-striped">
							<tr>
							<th>Failure Id: </th>
							<td>${item.failureId}</td>
							</tr>
							<tr>
							<th>Failure Type: </th>
							<td>${item.failureType}</td>
							</tr>
							<tr>
							<th>CFN 1 Level: </th>
							<td>${item.cfn1Level}</td>
							</tr>
							<tr>
							<th>CFN 2 Level: </th>
							<td>${item.cfn2Level}</td>
							</tr>
							<tr>
							<th>CFN 3 Level: </th>
							<td>${item.cfn3Level}</td>
							</tr>
							<tr>
							<th>Latitude: </th>
							<td>${item.latitude}</td>
							</tr>
							<tr>
							<th>Longitude: </th>
							<td>${item.longitude}</td>
							</tr>
						</table>`;
		return table;
	};

	const makeDetailsTableKpis = (item) => {
		const table = `<table class="table table-striped">

						<tr>
						<th>Level: </th>
						<td>${item.kpi}</td>
						</tr>

						<tr>
						<th>Latitude: </th>
						<td>${item.latitude}</td>
						</tr>

						<tr>
						<th>Longitude: </th>
						<td>${item.longitude}</td>
						</tr>

					</table>`;

		return table;
	};

	const _addLayerKpi = (map, data, legend, fitBounds, layerName) => {
		map.RemoveLayerById(layerName);
		const markers = [];

		if (!_.isEmpty(data)) {
			data.forEach(item => {
				const marker = L.circle([item.latitude, item.longitude], {
					color: item.color,
					fillColor: item.color,
					fillOpacity: 0.8,
					radius: 10
				});
				markers.push(marker.bindPopup(`<br>${makeDetailsTableKpis(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: layerName });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
			dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
		}
	};

	const _removeLayerById = (map, id) => {
		map.RemoveLayerById(id);
	};

	useMemo(() => {
		if (mapOne) {
			_removeLayerById(mapOne.control, 'topologyColors');
			_removeLayerById(mapOne.control, 'topologyColorsLabels');
			if (driveTest.driveTestLegend !== undefined) {
				if (driveTest.driveTestLegend.title !== 'none') {
					if (selectedDates.length > 0) {
						if (driveTest.driveTestLegend.title === 'empty') {
							dispatch(enqueueSnackbar({ message: 'No data found for UE', options: { variant: 'warning' } }));
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', false,
								'kpis'
							);
						} else if (formState.values.selectedKpiTech === '4g') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (driveTest.driveTestLegend.title === 'PCI') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTest.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTest.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTest.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTech === '3g') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (driveTest.driveTestLegend.title === 'SC') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTest.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTest.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTest.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTech === '2g') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTest.driveTestLegend !== undefined) {
								if (driveTest.driveTestLegend.title === 'BSIC') {
									const _map = mapOne.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTest.topologyColors.length !== 0) {
										mapOne.control.updateTopologyCarriersColors(driveTest.topologyColors, 'topologyColors');
										mapOne.control.addTopologyLabels(driveTest.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTech === 'common') {
							_addLayerKpi(
								mapOne.control, driveTest.driveTestPoints,
								driveTest.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
						}
					}
				} else {
					_addLayerKpi(
						mapOne.control, driveTest.driveTestPoints,
						driveTest.driveTestLegend || '', false,
						'kpis'
					);
				}
			}
		}
	}, [driveTest]);

	useMemo(() => {
		if (mapTwo) {
			_removeLayerById(mapTwo.control, 'topologyColors');
			if (driveTestCompare.driveTestLegend !== undefined) {
				if (driveTestCompare.driveTestLegend.title !== 'none') {
					if (selectedDatesCompare.length > 0) {
						if (driveTestCompare.driveTestLegend.title === 'empty') {
							dispatch(enqueueSnackbar({ message: 'No data found for UE', options: { variant: 'warning' } }));
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', false,
								'kpis'
							);
						} else if (formState.values.selectedKpiTechCompare === '4g') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestCompare.driveTestLegend !== undefined) {
								if (driveTestCompare.driveTestLegend.title === 'PCI') {
									const _map = mapTwo.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestCompare.topologyColors.length !== 0) {
										mapTwo.control.updateTopologyCarriersColors(driveTestCompare.topologyColors, 'topologyColors');
										mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTechCompare === '3g') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestCompare.driveTestLegend !== undefined) {
								if (driveTestCompare.driveTestLegend.title === 'SC') {
									const _map = mapTwo.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestCompare.topologyColors.length !== 0) {
										mapTwo.control.updateTopologyCarriersColors(driveTestCompare.topologyColors, 'topologyColors');
										mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTechCompare === '2g') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
							if (driveTestCompare.driveTestLegend !== undefined) {
								if (driveTestCompare.driveTestLegend.title === 'BSIC') {
									const _map = mapTwo.instance;
									const mapBounds = _map.getBounds();
									const mapBoundsPoints = {
										northEast: mapBounds._northEast,
										southWest: mapBounds._southWest
									};
									if (driveTestCompare.topologyColors.length !== 0) {
										mapTwo.control.updateTopologyCarriersColors(driveTestCompare.topologyColors, 'topologyColors');
										mapTwo.control.addTopologyLabels(driveTestCompare.topologyColors, 'topologyColorsLabels');
									}
								}
							}
						} else if (formState.values.selectedKpiTechCompare === 'common') {
							_addLayerKpi(
								mapTwo.control, driveTestCompare.driveTestPoints,
								driveTestCompare.driveTestLegend || '', formState.values.mapAutoZoom,
								'kpis'
							);
						}
					}
				} else {
					_addLayerKpi(
						mapTwo.control, driveTestCompare.driveTestPoints,
						driveTestCompare.driveTestLegend || '', false,
						'kpis'
					);
				}
			}
		}
	}, [driveTestCompare]);

	useEffect(() => {
		if (mapOne) {
			mapOne.instance.invalidateSize();
			mapTwo.instance.invalidateSize();
			mapOne.instance.sync(mapTwo.instance);
			mapTwo.instance.sync(mapOne.instance);
		}
		// dispatch(GetUtilizationDates());
	}, [formState.values.mapCompare, formState.values.mapFilters]);

	// if (mapOne) {
	// 	mapOne.instance.on('moveend', () => {
	// 		// callback
	// 		setTimeout(() => {
	// 			console.log('moveend');
	// 			const center = mapOne.instance.getCenter();
	// 			console.log(center);
	// 			mapTwo.instance.setView(center);
	// 		}, 1000);
	// 	});
	// }

	const _carriersColorsDialogClose = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleCarriersColorsDialog(false));
		}
	};

	const _carriersColorsPickerDialogClose = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleCarriersColorsPickerDialog(false));
			dispatch(ToggleCarriersColorsDialog(true));
		}
	};

	// console.log(formState.values.selectedKpiTech);
	// console.log(formState.values.selectedKpiScannerTech);

	let data;
	if (formState.values.selectedKpiTech !== undefined || formState.values.selectedKpiScannerTech !== undefined) {
		if (formState.values.selectedKpiTech !== undefined) {
			data = driveTest.chartData;
		}
	}

	// console.log(formState.values.selectedKpiTechCompare);
	// console.log(formState.values.selectedKpiScannerTechCompare);

	let dataCompare;
	if (formState.values.selectedKpiTechCompare !== undefined || formState.values.selectedKpiScannerTechCompare !== undefined) {
		if (formState.values.selectedKpiTechCompare !== undefined) {
			dataCompare = driveTestCompare.chartData;
		}
	}

	return (
		<Page className={classes.root} title="Map">
			<Header module="Modules" view="CAPACITY MANAGEMENT Algorithm" />
			{/* <Typography sx={{ lineHeight: 2 }}> TTI Utilization percentage aggregated by week on 07am till 12pm window.</Typography> */}
			{/* <div className={clsx(classes.container)}>
				 <Card className={classes.container}>
					<CardContent>
						{/* <Grid container spacing={1} style={{ marginBottom: '10px' }}>
							<Grid item md={12} xs={12}>
								<CompareBox />
							</Grid>
						</Grid> */}
			<Grid container spacing={1} style={{ marginBottom: '-10px' }}>
				<Grid item lg={12} md={12} sm={12}>
					<Card item className={classes.container}>
						<CardContent>
							<Grid container spacing={3} justifyContent="space-between" direction="row" alignItems="start">
								<Grid item lg={5} md={12} xs={12} className={classes.item}>
									<Typography sx={{ lineHeight: 1 }}> TTI Utilization percentage data aggregated by week on 06am till 12pm window.</Typography>
								</Grid>
								<Grid item className={classes.item} lg={1} md={12} xs={12}>

									<Typography lineHeight="2" variant="h6">
										Color Legend
									</Typography>

								</Grid>
								<Grid item lg={6} md={12} xs={12} className={classes.item}>
									<Grid container columnSpacing={3} justifyContent="space-evenly">

										<Grid item lg={3} md={3} xs={12}>
											<Grid container columnSpacing={2} direction="row" justifyContent="start" alignItems="center">
												<Grid className={classes.item}>
													<SquareIcon sx={{ color: '#00FF00' }} />
												</Grid>
												<Grid className={classes.item}>
													<Typography variant="body2">Low Load (0-50%)</Typography>
												</Grid>
											</Grid>
										</Grid>
										<Grid item lg={3} md={3} xs={12}>
											<Grid container columnSpacing={2} direction="row" justifyContent="start" alignItems="center">
												<Grid className={classes.item}>
													<SquareIcon sx={{ color: '#FFEC00' }} />
												</Grid>
												<Grid className={classes.item}>
													<Typography variant="body2">Medium Load (51-75%)</Typography>
												</Grid>
											</Grid>
										</Grid>
										<Grid item lg={3} md={3} xs={12}>
											<Grid container columnSpacing={2} direction="row" justifyContent="start" alignItems="center">
												<Grid className={classes.item}>
													<SquareIcon sx={{ color: '#FF7000' }} />
												</Grid>
												<Grid className={classes.item}>
													<Typography variant="body2">High Load (76-100%)</Typography>
												</Grid>
											</Grid>
										</Grid>
										<Grid item lg={3} md={3} xs={12}>
											<Grid container columnSpacing={2} direction="row" justifyContent="start" alignItems="center">
												<Grid className={classes.item}>
													<SquareIcon sx={{ color: '#FF0000' }} />
												</Grid>
												<Grid className={classes.item}>
													<Typography variant="body2">Cell Capacity Exceeded</Typography>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			<Grid container spacing={1} style={{ marginTop: '-20px', marginBottom: '10px' }}>
				<Grid item md={formState.values.mapCompare ? 6 : 12}>
					{/* <FiltersTop mapCompare={formState.values.mapCompare} /> */}
					<UtilizationDates id="date" />
				</Grid>
				<Grid item md={6} xs={6} hidden={!formState.values.mapCompare}>
					{/* <FiltersTopCompare mapCompare={formState.values.mapCompare} /> */}
					<UtilizationDatesCompare id="date-compare" />
				</Grid>
			</Grid>
			<Grid container spacing={1} style={{ marginBottom: '10px' }}>
				<Grid item md={2} xs={2} hidden={!formState.values.mapFilters}>
					<FiltersSide height="65vh" />
				</Grid>
				<Grid item md={formState.values.mapSize}>
					<MapCapacityGeneric id="dual-map" scale height="65vh" />
				</Grid>
				<Grid item md={formState.values.mapSize} hidden={!formState.values.mapCompare}>
					<MapCapacityGeneric id="dual-map-compare" scale height="65vh" />
				</Grid>
				<Grid item md={2} hidden={!formState.values.mapCompare || !formState.values.mapFilters}>
					<FiltersSideCompare height="65vh" />
				</Grid>
			</Grid>
			<Grid container spacing={1}>
				<Grid item md={12} xs={12}>
					{/* <AdditionalInfo data={data} dataCompare={dataCompare} /> */}
				</Grid>
			</Grid>

			{/* </CardContent>
				</Card>
				{carriersColorsDialogVisibility && (
					<CarriersColorsDialog
						onClose={_carriersColorsDialogClose}
						open={carriersColorsDialogVisibility}
					/>
				)}
				{carriersColorsPickerDialogVisibility && (
					<CarriersColorsPickerDialog
						onClose={_carriersColorsPickerDialogClose}
						open={carriersColorsPickerDialogVisibility}
						currentColorCode={currentColorCode}
					/>
				)}
			</div> */}
		</Page>
	);
};

export default DualMapGeneric;
